// connect-popup.ts — session 4 L1: the OAuth connect round trip (@vms/publishing 0.3.3, connect-CSRF safe).
//
// The package's callback COMPLETES NOTHING: it sends the browser back to the page that started the connect with the
// provider's answer in the fragment (#tf_connect=…). THIS page — running in whatever TubeForge session this browser
// has — completes it: POST /publishing/channels/connect/complete with the user's bearer. The server accepts it only
// when the attempt was started by this same user; a victim who opened an attacker's link is refused.
//
// Runs before React mounts (main.tsx). In the connect popup: complete, report to the opener, close. In a full window
// (the link was opened directly): complete, keep the result for the page, clear the fragment, boot as usual.
import { apiPost } from "@/api/client.ts";

export const CONNECT_MESSAGE = "tf-connect-result";
// (audit 10-17 B-F3) the result's shape and the kept-result read are pure (lib/connect-result.ts), loadable without the API client
export { CONNECT_RESULT_KEY, takeKeptConnectResult, type ConnectResult } from "./connect-result.ts";
import { CONNECT_RESULT_KEY, type ConnectResult } from "./connect-result.ts";
import { w } from "@/lib/words.ts";   // (B107)

function relayed(): { channel: string; state: string; params: Record<string, string> } | null {
  const m = /(?:^#|&)tf_connect=([A-Za-z0-9_-]+)/.exec(window.location.hash);
  if (!m) return null;
  try { return JSON.parse(atob(m[1]!.replace(/-/g, "+").replace(/_/g, "/"))); } catch { return null; }  // catch-reason: a state token that isn't JSON is absent (no state)
}

async function complete(r: { channel: string; state: string; params: Record<string, string> }): Promise<ConnectResult> {
  try {
    const out = await apiPost<{ channel: { channel: string; status: string } }>("/publishing/channels/connect/complete", r);
    return { connected: out.channel.channel, status: out.channel.status };
  } catch (e) {  // catch-reason: the failure IS the answer: { error } names the connect failure
    // The package answers { error: "<code>", message }; api/client.ts carries a string `error` as the message.
    const m = (e as { message?: string }).message ?? "";
    return { error: /^[a-z_]{3,40}$/.test(m) ? m : "connect_failed" };
  }
}

/**
 * Handle a relayed connect answer, if this page carries one. Returns true when this window was the popup (it closes;
 * the app must not boot). False: no answer, or a full window that has finished and should boot normally.
 */
export async function finishConnectIfAny(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const r = relayed();
  if (!r) return false;
  history.replaceState(null, "", window.location.pathname + window.location.search);   // the code leaves the URL now
  const result: ConnectResult = { ...(await complete(r)), channel: r.channel };
  const isPopup = !!window.opener && window.opener !== window;
  if (isPopup) {
    try { window.opener.postMessage({ type: CONNECT_MESSAGE, result }, window.location.origin); } catch { /* opener gone */ }
    window.close();
    return true;
  }
  try { sessionStorage.setItem(CONNECT_RESULT_KEY, JSON.stringify(result)); } catch { /* storage off */ }
  return false;
}

/** Customer-facing words for the package's connect error codes (@vms/publishing 0.3.1–0.3.3). */
export function connectErrorText(code: string): string {
  switch (code) {
    // (B107) the words through w(): editable in Admin → Words
    case "account_owned_elsewhere": return w("page.connect.taken");
    case "attempt_owner_mismatch": return w("page.connect.other_account");
    case "consent_denied": return w("page.connect.not_approved");
    case "unknown_attempt": case "invalid_state": return w("page.connect.expired");
    case "return_url_not_allowed": return w("page.connect.not_started");
    default: return w("page.connect.failed");
  }
}

/**
 * (tf3-26a) Start a connect through the package's flow in a popup: POST /publishing/channels/connect with this page as
 * the return URL, then send the popup to the platform. CALL IT DIRECTLY INSIDE THE CLICK — the popup is opened before
 * any await (a window opened after an await is blocked). Resolves when the popup has closed; rejects with the refusal
 * (e.g. the plan's channel limit), the popup closed.
 */
export async function connectInPopup(channel: string, start: (channel: string, returnUrl: string) => Promise<{ authorizeUrl: string }>): Promise<void> {
  const popup = window.open("about:blank", "tf-connect", "width=520,height=720");
  try {
    const { authorizeUrl } = await start(channel, window.location.href);
    if (popup) popup.location.href = authorizeUrl; else window.location.href = authorizeUrl;
  } catch (e) { popup?.close(); throw e; }
  await new Promise<void>((done) => { const t = window.setInterval(() => { if (!popup || popup.closed) { window.clearInterval(t); done(); } }, 1000); });
}
